import { EBrandingLevel } from 'node/repositories/offer-card/entities/all_offer_data/AllOfferDataSchema';

import { IAmenitiesData } from 'shared/types/amenities';
import {
  IGetOfferDataResponseSchema,
  IOfferSchema as DEPRECATED__IOfferSchema,
  IAgentSchema,
  IPhotoSchema,
  TCategory,
  IBargainTermsSchema,
} from 'shared/types/api-models/offer-card/v1/get-offer-data';
import { ICoworkingAmenitiesData } from 'shared/types/coworkingAmenities';

import { IAreaPart } from './areaParts';
import { IFactoidsData } from './factoid';
import { IFeaturesData } from './features';
import { IGeo } from './geo/geo';
import { INewBuilding } from './newbuilding';
import { INewbuildingBroker } from './newbuildingBroker';
import { ICompanyWithFeatures } from './newbuildingCompany';
import { INewBuildingWithFeatures } from './newobject/newobject';
import { IOffersComparisonStatus } from './offersComparisonStatus';
import { IProfitbaseBooking } from './profitbaseBooking/profitbaseBooking';
import { ISidebarData } from './sidebar';
import { IAgentMetricSchema } from '../../node/repositories/offer-card/entities/agent/AgentMetricSchema';
import { IBtiDataSchema } from '../../node/repositories/offer-card/entities/bti_data/BtiDataSchema';
import { IModerationInfoSchema } from '../../node/repositories/offer-card/entities/moderation_info/ModerationInfoSchema';
import { IGetOfferDataResponse } from '../../node/repositories/offer-card/v1/get-offer-data';
import { IAgentModerationInfoSchema } from '../repositories/offer-card/entities/agent/AgentModerationInfoSchema';
import { IOfferSchema } from '../repositories/offer-card/entities/offer/OfferSchema';

// coworkingRent - это computed категория, так как коворкинги это подтип officeRent
export type TCategoryExtended = TCategory | 'coworkingRent' | 'decoration';

export interface IBtiData {
  bti?: IBtiDataSchema | null;
}

export interface IFolderData {
  offerFoldersCount: number;
  removedFromFavorites: boolean;
}

export enum EDealRentMonetization {
  /** Комиссию платит собственник **/
  FeeByHomeowner = 'feeByHomeowner',
  /** Комиссию платит арендатор **/
  FeeByRenter = 'feeByRenter',
  FeeIncludedInOfferPrice = 'feeIncludedInOfferPrice',
}

type IOfferSchemaForExtend = Omit<DEPRECATED__IOfferSchema, 'geo' | 'areaParts'>;

export interface IOffer extends IOfferSchemaForExtend {
  newbuilding: INewBuilding;
  cianLayouts: IPhotoSchema[] | null;
  moderationInfo?: IModerationInfoSchema | null;
  dealRentMonetization?: EDealRentMonetization | null;
  isDealRentOffline?: boolean | null;
  isUnique?: boolean | null;
  /**
   * @todo Удалить строку с экспериментом
   * @description Данный функционал появился в задаче CD-128336, будет удалён в задаче CD-128344
   * Если эксперимент будет удачным, то оставить строку, но удалить комментарий,
   * если эксперимент будет не удачным, то удалить полностью строку
   */
  hasFairPrice?: boolean;
  timezone?: number | null;
  villageMortgageAllowed?: boolean | null;
  bargainTerms: IBargainTerms;
  geo: IGeo;
  areaParts?: IAreaPart[];
  externalOfferUrl: string;
  isCianPartner: boolean;
  /** Признак уникальности объявления Коммерческой **/
  isUniqueForCian?: IOfferSchema['isUniqueForCian'];

  /** Признак наличия отчета от Best Place для объявки (коммерческая) **/
  bestPlaceAnalyticsAvailable: boolean | null;
}

export interface IBargainTerms extends IBargainTermsSchema {
  tags?: string[];
  deposit?: number | null;
}

export interface IAgent extends IAgentSchema {
  moderationInfo?: IAgentModerationInfoSchema | null;
  metrics?: IAgentMetricSchema[] | null;
  agentLists?: string[] | null;
}

export interface IRosreestrElement {
  title: string;
  value: string;
}

export interface IRosreestrPart {
  header: string;
  elements: IRosreestrElement[];
}

export interface IRosreestrAnalyticsData {
  status: number;
  updateDate: string;
  ownersNum: number;
  buildingType: string;
  landCategory: string;
  encumbrances: boolean;
}

export interface ICadastralMap {
  /** Расширенное превью для кадастровой карты 1000x1000 **/
  fullUrl?: string | null;
  /** Превью для кадастровой карты 512x512 **/
  previewUrl: string;
}

export interface IRosreestrInfo {
  deck: string;
  header: string;
  parts: IRosreestrPart[];
  analyticsMeta: IRosreestrAnalyticsData;
  cadastralMapPreview?: ICadastralMap | null;
  bottom?: string;
}

export interface IRosreestrData {
  rosreestrInfo: IRosreestrInfo | null;
}

export interface IExternalOfferUrl {
  externalOfferUrl?: string | null;
}

export type IOfferData = Omit<IGetOfferDataResponseSchema, 'housePhotos' | 'newObject'> &
  Pick<
    IGetOfferDataResponse,
    | 'amenities'
    | 'coworkingIncludedAmenities'
    | 'decorationInfo'
    | 'housingDecorations'
    | 'tourV2'
    | 'houseCardEntrypointData'
    | 'newbuildingFlatViewOrder'
    | 'transportAccessibility'
    | 'priceInfo'
    | 'floorPlan'
    | 'isOnlineRegistrationAvailable'
    | 'isBookedFromDeveloper'
    | 'factoids'
    | 'features'
    | 'formattedAreaParts'
    | 'sidebar'
    /**
     * @todo Удалить блок с экспериментом
     * @description Данный функционал появился в задаче CD-129223, будет удалён в задаче CD-129224
     * Удалить в случае, если эксперимент будет не удачным, так же потребуется обновить сваггер генерацию,
     * что бы убрать это поле
     */
    | 'userPhoneShowStatus'
  > & {
    company: ICompanyWithFeatures | null;
    offer: IOffer;
    newObject: INewBuildingWithFeatures | null;
    profitbaseBooking?: IProfitbaseBooking;
    offersComparisonStatus: IOffersComparisonStatus | null;
    agent: IAgent | null;
    brandingLevel?: EBrandingLevel | null;
    trackingUrl: string;
  } & IBtiData &
  IFolderData &
  IRosreestrData &
  IFactoidsData &
  IFeaturesData &
  IAmenitiesData &
  ICoworkingAmenitiesData &
  ISidebarData & {
    newbuildingBroker?: INewbuildingBroker;
  };

export type IValidPhone = {
  countryCode: string;
  number: string;
};

export interface ISpecialProjectItemSchema {
  id: string;
  link: string;
}

export { EBrandingLevel };

export type TFeatureTheme =
  | 'toilet'
  | 'fridge'
  | 'dishwasher'
  | 'washing'
  | 'cond'
  | 'tv'
  | 'internet'
  | 'bath'
  | 'shower'
  | 'kitchen'
  | 'furniture'
  | 'banya'
  | 'garage'
  | 'pool'
  | 'car-wash'
  | 'car-service'
  | 'pharmacy'
  | 'atm'
  | 'buffet'
  | 'exhibition-warehouse-complex'
  | 'hotel'
  | 'game-room'
  | 'cafe'
  | 'cinema'
  | 'conference-room'
  | 'medical-center'
  | 'market'
  | 'notary-office'
  | 'bank-department'
  | 'office-space'
  | 'park'
  | 'beauty-shop'
  | 'warehouse'
  | 'fitness-centre'
  | 'photo-studio'
  | 'central-reception'
  | 'clothes-studio'
  | 'baby-sitting'
  | 'bowling'
  | 'video-surveillance'
  | 'entry-by-pass'
  | 'slot-machines'
  | 'rink'
  | 'secure'
  | 'basement'
  | 'inspection-pit'
  | 'tire'
  | 'automatic-gates'
  | 'aquapark';

export interface IFeatureItem {
  theme?: string;
  name: string;
  value?: string;
}
